import { useCallback, useMemo } from 'react';
import queryString from 'query-string';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

const withUndefinedRemoved = (
  object: Record<string, string | undefined>
): Record<string, string> => {
  const result = { ...object };
  Object.keys(result).forEach((key) => {
    if (result[key] === undefined) {
      delete result[key];
    }
  });
  return result as Record<string, unknown> as Record<string, string>;
};

type QueryInput = Record<string, string | string[]> | string;

const toSearchString = (query: QueryInput) => {
  const qs =
    typeof query === 'string'
      ? query.replace(/^\?/, '')
      : queryString.stringify(query, { arrayFormat: 'comma' });
  return qs ? `?${qs}` : '';
};

export const useRouter = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();

  const location = useLocation();

  const query = useMemo(() => {
    const combined: Record<string, string | string[]> = withUndefinedRemoved(params);
    searchParams.forEach((value, key) => {
      if (key in combined) {
        const existingValue = combined[key];
        if (value !== existingValue) {
          combined[key] = Array.isArray(existingValue)
            ? [...existingValue, value]
            : [existingValue, value];
        }
      } else {
        combined[key] = value;
      }
    });
    return combined;
  }, [params, searchParams]);

  const navigate = useNavigate();

  const push = useCallback(
    (
      params:
        | {
            pathname: string;
            query?: Record<string, string> | string;
          }
        | string
    ) => {
      if (typeof params === 'string') {
        navigate(params);
        return;
      }
      const { pathname, query } = params;
      if (query !== undefined) {
        const search = toSearchString(query);
        navigate({ pathname, search });
        return;
      }
      navigate(pathname);
    },
    [navigate]
  );

  // equivlant to Next.js useRouter hook's route property
  const routes = useMemo(() => location.pathname.split('/'), [location.pathname]);

  const replace = useCallback(
    ({
      pathname,
      query,
    }: {
      pathname?: string;
      query?: Record<string, string | string[]> | string;
    }) => {
      const targetPath = pathname ?? location.pathname;
      if (query !== undefined) {
        const search = toSearchString(query);
        navigate({ pathname: targetPath, search }, { replace: true });
        return;
      }
      if (pathname) {
        navigate(pathname, { replace: true });
      }
    },
    [navigate, location.pathname]
  );
  const back = useCallback(
    (step = -1) => {
      navigate(step);
    },
    [navigate]
  );

  const router = useMemo(
    () => ({
      pathname: location.pathname,
      push,
      routes,
      query,
      params,
      searchParams,
      replace,
      back,
    }),
    [location, push, routes, query, params, searchParams, replace, back]
  );
  return router;
};
